/**
 * Available automation events in the system
 */

export const AUTOMATION_EVENTS = {
  // Lead Creation Events
  LEAD_CREATED: "lead.created",
  LEAD_CREATED_FROM_WHATSAPP: "lead.created_from_whatsapp",
  LEAD_CREATED_FROM_MANUAL: "lead.created_from_manual",

  // Grade Status Change Events
  GRADE_CHANGED_TO_HOT: "grade.changed.to_hot",
  GRADE_CHANGED_TO_WARM: "grade.changed.to_warm",
  GRADE_CHANGED_TO_COLD: "grade.changed.to_cold",
  GRADE_CHANGED_TO_DROPPED: "grade.changed.to_dropped",
  GRADE_CHANGED_FROM_DROPPED: "grade.changed.from_dropped",

  // Pipeline Status Change Events
  PIPELINE_CHANGED_TO_QUALIFIED: "pipeline.changed.to_qualified",
  PIPELINE_CHANGED_TO_NEED_ANALYSIS: "pipeline.changed.to_need_analysis",
  PIPELINE_CHANGED_TO_BOOKED: "pipeline.changed.to_booked",
  PIPELINE_CHANGED_TO_POST_CONSULT: "pipeline.changed.to_post_consult",
  PIPELINE_CHANGED_TO_PROPOSAL_SHARED: "pipeline.changed.to_proposal_shared",
  PIPELINE_CHANGED_TO_NEGOTIATION: "pipeline.changed.to_negotiation",
  PIPELINE_CHANGED_TO_TREATMENT_BOOKED: "pipeline.changed.to_treatment_booked",
  PIPELINE_CHANGED_TO_WON: "pipeline.changed.to_won",
  PIPELINE_CHANGED_TO_DROPPED: "pipeline.changed.to_dropped",

  // Booking Events
  BOOKING_CREATED: "booking.created",
  BOOKING_STATUS_CHANGED_TO_CONFIRMED: "booking.status.changed.to_confirmed",
  BOOKING_STATUS_CHANGED_TO_ONGOING: "booking.status.changed.to_ongoing",
  BOOKING_STATUS_CHANGED_TO_COMPLETED: "booking.status.changed.to_completed",
  BOOKING_STATUS_CHANGED_TO_CANCELLED: "booking.status.changed.to_cancelled",

  // Consultancy Events
  CONSULTANCY_BOOKED: "booking.consultancy.created",
  CONSULTANCY_SCHEDULED: "booking.consultancy.scheduled",
  CONSULTANCY_CONFIRMED: "booking.consultancy.confirmed",
  CONSULTANCY_COMPLETED: "booking.consultancy.completed",
  CONSULTANCY_CANCELLED: "booking.consultancy.cancelled",

  // Treatment Events
  TREATMENT_BOOKED: "booking.treatment.created",
  TREATMENT_SCHEDULED: "booking.treatment.scheduled",
  TREATMENT_CONFIRMED: "booking.treatment.confirmed",
  TREATMENT_COMPLETED: "booking.treatment.completed",
  TREATMENT_CANCELLED: "booking.treatment.cancelled",

  // Payment Events
  PAYMENT_CREATED: "payment.created",
  PAYMENT_CREATED_FIRST_PAYMENT: "payment.created.first_payment",
  PAYMENT_CREATED_FULL_PAYMENT: "payment.created.full_payment",

  // Task/Follow-up Events
  TASK_CREATED: "task.created",
  FOLLOWUP_DUE: "followup.due",
  FOLLOWUP_OVERDUE: "followup.overdue",
  FOLLOWUP_RESCHEDULED: "followup.rescheduled",
};

/**
 * Event categories for grouping in UI
 */
export const EVENT_CATEGORIES = [
  {
    label: "Lead Creation",
    value: "lead_creation",
    events: [
      { key: AUTOMATION_EVENTS.LEAD_CREATED, label: "Lead Created" },
      { key: AUTOMATION_EVENTS.LEAD_CREATED_FROM_WHATSAPP, label: "Lead from WhatsApp" },
      { key: AUTOMATION_EVENTS.LEAD_CREATED_FROM_MANUAL, label: "Lead from Manual Entry" },
    ],
  },
  {
    label: "Consultancy Events",
    value: "consultancy_events",
    events: [
      { key: AUTOMATION_EVENTS.CONSULTANCY_BOOKED, label: "Consultancy Booked" },
      { key: AUTOMATION_EVENTS.CONSULTANCY_SCHEDULED, label: "Consultancy Scheduled" },
      { key: AUTOMATION_EVENTS.CONSULTANCY_CONFIRMED, label: "Consultancy Confirmed" },
      { key: AUTOMATION_EVENTS.CONSULTANCY_COMPLETED, label: "Consultancy Completed" },
      { key: AUTOMATION_EVENTS.CONSULTANCY_CANCELLED, label: "Consultancy Cancelled" },
    ],
  },
  {
    label: "Treatment Events",
    value: "treatment_events",
    events: [
      { key: AUTOMATION_EVENTS.TREATMENT_BOOKED, label: "Treatment Booked" },
      { key: AUTOMATION_EVENTS.TREATMENT_SCHEDULED, label: "Treatment Scheduled" },
      { key: AUTOMATION_EVENTS.TREATMENT_CONFIRMED, label: "Treatment Confirmed" },
      { key: AUTOMATION_EVENTS.TREATMENT_COMPLETED, label: "Treatment Completed" },
      { key: AUTOMATION_EVENTS.TREATMENT_CANCELLED, label: "Treatment Cancelled" },
    ],
  },
  {
    label: "Follow-up & Scheduling",
    value: "followup_events",
    events: [
      { key: AUTOMATION_EVENTS.FOLLOWUP_DUE, label: "Follow-up Due" },
      { key: AUTOMATION_EVENTS.FOLLOWUP_OVERDUE, label: "Follow-up Overdue" },
      { key: AUTOMATION_EVENTS.FOLLOWUP_RESCHEDULED, label: "Follow-up Rescheduled" },
    ],
  },
];

/**
 * Available lead fields for variable mapping
 */
export const LEAD_FIELDS = [
  { label: "Client Name", value: "client_name", category: "Basic" },
  { label: "Contact", value: "contact", category: "Basic" },
  { label: "Email", value: "email", category: "Basic" },
  { label: "Services", value: "services", category: "Basic" },
  { label: "Priority", value: "priority", category: "Basic" },
  { label: "Created At", value: "created_at", category: "Basic" },
  { label: "Lead Source", value: "custom_fields.lead_source", category: "Custom Fields" },
  { label: "City", value: "custom_fields.city", category: "Custom Fields" },
  { label: "Assign Employee Name", value: "assign_employee.name", category: "Employee" },
  { label: "Assign Employee Email", value: "assign_employee.email", category: "Employee" },
  { label: "Assign Employee Phone", value: "assign_employee.phone", category: "Employee" },
  { label: "Current Status Type", value: "current_status.type", category: "Status" },
  { label: "Current Status Key", value: "current_status.key", category: "Status" },
  { label: "Booking Type", value: "bookings.-1.type", category: "Booking" },
  { label: "Booking Date", value: "bookings.-1.date", category: "Booking" },
  { label: "Booking Slot", value: "bookings.-1.slot", category: "Booking" },
  { label: "Booking Duration", value: "bookings.-1.duration", category: "Booking" },
  { label: "Consultant Name", value: "bookings.-1.consultant.name", category: "Booking" },
  { label: "Consultant Email", value: "bookings.-1.consultant.email", category: "Booking" },
  { label: "Consultant Phone", value: "bookings.-1.consultant.phone", category: "Booking" },
  { label: "Payment Amount", value: "payments.-1.amount", category: "Payment" },
  { label: "Payment Mode", value: "payments.-1.mode", category: "Payment" },
  { label: "Payment Receipt", value: "payments.-1.receipt", category: "Payment" },
  { label: "Payment Date", value: "payments.-1.created_at", category: "Payment" },
  { label: "Total Payments", value: "payments.length", category: "Payment (Computed)" },
  { label: "Total Amount Paid", value: "total_amount_paid", category: "Payment (Computed)" },
  { label: "Next Follow-up Date", value: "next_followup", category: "Follow-up" },
];

/**
 * Condition operators
 */
export const CONDITION_OPERATORS = [
  { label: "Equals", value: "eq" },
  { label: "Not Equals", value: "ne" },
  { label: "Contains", value: "contains" },
  { label: "Greater Than", value: "gt" },
  { label: "Greater Than or Equal", value: "gte" },
  { label: "Less Than", value: "lt" },
  { label: "Less Than or Equal", value: "lte" },
  { label: "Exists", value: "exists" },
  { label: "In List", value: "in" },
];

/**
 * Transform options
 */
export const TRANSFORM_OPTIONS = [
  { label: "None", value: "" },
  { label: "Uppercase", value: "uppercase" },
  { label: "Lowercase", value: "lowercase" },
  { label: "Title Case", value: "titlecase" },
  { label: "Date Format", value: "date_format" },
  { label: "Currency", value: "currency" },
  { label: "Join Array", value: "join" },
];

/**
 * Suggested templates for events
 */
export const SUGGESTED_TEMPLATES = {
  [AUTOMATION_EVENTS.LEAD_CREATED]: "welcome_template",
  [AUTOMATION_EVENTS.LEAD_CREATED_FROM_WHATSAPP]: "webhook_welcome",
  [AUTOMATION_EVENTS.PIPELINE_CHANGED_TO_BOOKED]: "consultation_booked",
  [AUTOMATION_EVENTS.PIPELINE_CHANGED_TO_POST_CONSULT]: "post_consult_followup",
  [AUTOMATION_EVENTS.PIPELINE_CHANGED_TO_WON]: "won_thank_you",
  [AUTOMATION_EVENTS.PIPELINE_CHANGED_TO_DROPPED]: "lost_lead_feedback",
  [AUTOMATION_EVENTS.BOOKING_STATUS_CHANGED_TO_COMPLETED]: "session_completed_feedback",
  [AUTOMATION_EVENTS.PAYMENT_CREATED]: "payment_received_confirmation",
  [AUTOMATION_EVENTS.PAYMENT_CREATED_FIRST_PAYMENT]: "first_payment_welcome",
  [AUTOMATION_EVENTS.PAYMENT_CREATED_FULL_PAYMENT]: "payment_completed_thank_you",
  [AUTOMATION_EVENTS.FOLLOWUP_DUE]: "followup_reminder",
  [AUTOMATION_EVENTS.FOLLOWUP_OVERDUE]: "followup_overdue",
  [AUTOMATION_EVENTS.FOLLOWUP_RESCHEDULED]: "followup_rescheduled",
};

/**
 * Get event label by key
 */
export const getEventLabel = (eventKey) => {
  for (const category of EVENT_CATEGORIES) {
    const event = category.events.find((e) => e.key === eventKey);
    if (event) return event.label;
  }
  return eventKey;
};
